import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AppContext } from '../App';
import { Save, Database, Key, AlertCircle, DollarSign, RefreshCw, Check, X, Pencil, Loader2, Trash2, Eye, EyeOff, Activity, CheckCircle, XCircle, Bell, RotateCcw } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const Settings = () => {
  const { API } = React.useContext(AppContext);
  const [settings, setSettings] = useState({
    sql_server_host: '',
    sql_server_user: '',
    sql_server_password: '',
    sql_server_database: '',
    emergent_llm_key: ''
  });
  const [currentSettings, setCurrentSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingKey, setDeletingKey] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  
  // AI Chat Diagnostics state
  const [diagnostics, setDiagnostics] = useState(null);
  const [runningDiagnostics, setRunningDiagnostics] = useState(false);
  
  // Pay Types state
  const [payTypes, setPayTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingPayTypes, setLoadingPayTypes] = useState(true);
  const [savingPayTypes, setSavingPayTypes] = useState(false);
  const [editingPayType, setEditingPayType] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [hasPayTypeChanges, setHasPayTypeChanges] = useState(false);
  const [showRefreshDialog, setShowRefreshDialog] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Insight Thresholds state
  const [thresholdValues, setThresholdValues] = useState({});
  const [thresholdDefs, setThresholdDefs] = useState([]);
  const [thresholdEdits, setThresholdEdits] = useState({});
  const [savingThresholds, setSavingThresholds] = useState(false);

  const runDiagnostics = useCallback(async () => {
    setRunningDiagnostics(true);
    setDiagnostics(null);
    try {
      const response = await axios.get(`${API}/chat/diagnostics`);
      setDiagnostics(response.data);
    } catch (error) {
      setDiagnostics({
        test_result: 'FAILED',
        error: error.response?.data?.detail || error.message,
        error_type: 'request_failed'
      });
    } finally {
      setRunningDiagnostics(false);
    }
  }, [API]);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/settings`);
      setCurrentSettings(response.data);
      setSettings({
        sql_server_host: response.data.sql_server_host || '',
        sql_server_user: response.data.sql_server_user || '',
        sql_server_password: '',
        sql_server_database: response.data.sql_server_database || '',
        emergent_llm_key: ''
      });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching settings:', error);
      }
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [API]);

  const fetchPayTypes = useCallback(async () => {
    setLoadingPayTypes(true);
    try {
      const response = await axios.get(`${API}/pay-types`);
      setPayTypes(response.data.pay_types || []);
      setCategories(response.data.categories || []);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching pay types:', error);
      }
      toast.error('Failed to load pay types');
    } finally {
      setLoadingPayTypes(false);
    }
  }, [API]);

  const fetchThresholds = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/insights/thresholds`);
      setThresholdValues(response.data.values || {});
      setThresholdDefs(response.data.definitions || []);
      setThresholdEdits(response.data.values || {});
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching thresholds:', error);
      }
    }
  }, [API]);

  const saveThresholds = async () => {
    setSavingThresholds(true);
    try {
      // Coerce to numbers
      const payload = Object.fromEntries(
        Object.entries(thresholdEdits).map(([k, v]) => [k, Number(v)])
      );
      const response = await axios.post(`${API}/insights/thresholds`, { thresholds: payload });
      setThresholdValues(response.data.thresholds);
      setThresholdEdits(response.data.thresholds);
      // Invalidate cache so insights regenerate with the new thresholds
      try {
        await axios.post(`${API}/cache/invalidate`);
      } catch (cacheErr) {
        // Cache invalidation is best-effort — server cache will expire by TTL anyway.
        if (process.env.NODE_ENV === 'development') {
          console.warn('Cache invalidation after threshold save failed:', cacheErr);
        }
      }
      toast.success('Thresholds saved — Critical Alerts will recalculate on next load');
    } catch (error) {
      console.error('Error saving thresholds:', error);
      toast.error('Failed to save thresholds');
    } finally {
      setSavingThresholds(false);
    }
  };

  const resetThresholdsToDefault = () => {
    const defaults = Object.fromEntries(
      thresholdDefs.map(d => [d.id, d.default])
    );
    setThresholdEdits(defaults);
  };

  // Fetch settings and pay types on mount
  useEffect(() => {
    fetchSettings();
    fetchPayTypes();
    fetchThresholds();
  }, [fetchSettings, fetchPayTypes, fetchThresholds]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = Object.fromEntries(
        Object.entries(settings).filter(([_, v]) => v !== '')
      );

      await axios.post(`${API}/settings`, updates);
      
      // Show appropriate message based on what was changed
      const changedKeys = Object.keys(updates);
      const onlyApiKey = changedKeys.length === 1 && changedKeys[0] === 'emergent_llm_key';
      const includesDbSettings = changedKeys.some(k => k.startsWith('sql_server_'));
      
      if (onlyApiKey) {
        toast.success('API key updated! Changes are active immediately.');
      } else if (includesDbSettings) {
        toast.success('Settings saved! Database connection changes require a backend restart.');
      } else {
        toast.success('Settings saved!');
      }
      
      fetchSettings();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error saving settings:', error);
      }
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  // Pay Type handlers
  const [pendingToggle, setPendingToggle] = useState(null); // { payType, willBePremium }

  const startEditPayType = (payType) => {
    setEditingPayType(payType.pay_type);
    setEditForm({
      pay_type: payType.pay_type,
      category: payType.category || '',
      premium_flag: payType.premium_flag,
      multiplier: payType.multiplier,
      display_order: payType.display_order,
      description: payType.description || ''
    });
  };

  const cancelEditPayType = () => {
    setEditingPayType(null);
    setEditForm({});
  };

  const savePayType = async () => {
    setSavingPayTypes(true);
    try {
      await axios.put(`${API}/pay-types/${encodeURIComponent(editForm.pay_type)}`, editForm);
      toast.success(`Pay type "${editForm.pay_type}" updated`);
      setEditingPayType(null);
      setEditForm({});
      setHasPayTypeChanges(true);
      fetchPayTypes();
    } catch (error) {
      console.error('Error saving pay type:', error);
      toast.error('Failed to save pay type');
    } finally {
      setSavingPayTypes(false);
    }
  };

  // Trigger from inline toggle click — opens confirmation dialog instead of
  // writing immediately. The Switch UI will reflect the new value only after
  // the user confirms; we revert via a forced re-render via fetchPayTypes().
  const togglePremiumFlag = (payType) => {
    setPendingToggle({
      payType,
      willBePremium: !payType.premium_flag
    });
  };

  const cancelPendingToggle = () => {
    setPendingToggle(null);
    // Force the Switch back to its DB-truth state
    fetchPayTypes();
  };

  // Confirmed: write to backend, clear caches, force a full app refresh so
  // every chart, KPI, filter, and AI recommendation regenerates.
  const confirmPendingToggle = async () => {
    if (!pendingToggle) return;
    const { payType, willBePremium } = pendingToggle;
    setSavingPayTypes(true);
    try {
      const updatedPayType = { ...payType, premium_flag: willBePremium };
      await axios.put(
        `${API}/pay-types/${encodeURIComponent(payType.pay_type)}`,
        updatedPayType
      );

      // Backend already invalidated server-side caches on the PUT.
      // Clear ALL client-side sessionStorage so every cached page (dashboard,
      // insights_actions, recommendations, trend_analysis, etc.) refetches.
      try {
        sessionStorage.clear();
      } catch (storageErr) {
        // Storage clear is best-effort (Safari private mode may block); page reload
        // below still forces a re-fetch from server.
        if (process.env.NODE_ENV === 'development') {
          console.warn('sessionStorage.clear() failed (likely private mode):', storageErr);
        }
      }

      toast.success(
        `${payType.pay_type} ${willBePremium ? 'now Premium' : 'now Non-Premium'} — refreshing analytics...`
      );

      // Brief pause so the toast is visible, then full reload to the dashboard.
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 1200);
    } catch (error) {
      console.error('Error toggling premium flag:', error);
      toast.error('Failed to update pay type');
      setSavingPayTypes(false);
      setPendingToggle(null);
      fetchPayTypes();
    }
  };

  const handleRefreshAnalytics = async () => {
    setRefreshing(true);
    try {
      // Clear all cached data from sessionStorage
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('overtimeiq_')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => sessionStorage.removeItem(key));
      
      toast.success('Analytics cache cleared. Dashboards will refresh with updated pay type settings.');
      setShowRefreshDialog(false);
      setHasPayTypeChanges(false);
      
      // Small delay then redirect to dashboard
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 1500);
    } catch (error) {
      console.error('Error refreshing analytics:', error);
      toast.error('Failed to refresh analytics');
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="settings-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-1">Settings</h1>
        <p className="text-muted-foreground">Configure database connection, pay types, and API keys</p>
      </div>

      {/* Alert */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3"
      >
        <AlertCircle size={20} className="text-blue-600 mt-0.5" />
        <div>
          <p className="text-sm text-blue-900 font-medium">Configuration Note</p>
          <p className="text-sm text-blue-700 mt-1">
            <strong>API Key changes</strong> take effect immediately. <strong>Database connection changes</strong> require a backend restart.
            Only fill in fields you want to update - empty fields will not be changed.
          </p>
        </div>
      </motion.div>

      <div className="space-y-6">
        {/* Pay Types Configuration */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="kpi-card"
          data-testid="pay-types-section"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#4CAF93]/10 rounded-lg flex items-center justify-center">
                <DollarSign size={20} className="text-[#4CAF93]" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">Pay Type Configuration</h2>
                <p className="text-sm text-muted-foreground">Configure which pay types are included in premium analysis</p>
              </div>
            </div>
            
            {hasPayTypeChanges && (
              <Button 
                onClick={() => setShowRefreshDialog(true)}
                className="bg-[#4CAF93] hover:bg-[#3d9c7d]"
                data-testid="refresh-analytics-btn"
              >
                <RefreshCw size={16} className="mr-2" />
                Refresh Analytics
              </Button>
            )}
          </div>

          {loadingPayTypes ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="pay-types-table">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Pay Type</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Category</th>
                    <th className="text-center py-3 px-4 font-medium text-muted-foreground">Premium</th>
                    <th className="text-center py-3 px-4 font-medium text-muted-foreground">Multiplier</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Description</th>
                    <th className="text-center py-3 px-4 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payTypes.map((pt) => (
                    <tr 
                      key={pt.pay_type} 
                      className={`border-b border-border/50 hover:bg-muted/30 ${
                        pt.premium_flag ? 'bg-[#4CAF93]/5' : ''
                      }`}
                      data-testid={`pay-type-row-${pt.pay_type}`}
                    >
                      {editingPayType === pt.pay_type ? (
                        // Edit mode
                        <>
                          <td className="py-3 px-4 font-medium text-foreground">
                            {pt.pay_type}
                          </td>
                          <td className="py-3 px-4">
                            <select
                              value={editForm.category}
                              onChange={(e) => setEditForm({...editForm, category: e.target.value})}
                              className="w-full px-2 py-1 border border-input rounded text-sm"
                            >
                              {categories.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                              ))}
                            </select>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Switch
                              checked={editForm.premium_flag}
                              onCheckedChange={(checked) => setEditForm({...editForm, premium_flag: checked})}
                            />
                          </td>
                          <td className="py-3 px-4">
                            <input
                              type="number"
                              step="0.05"
                              min="0"
                              max="5"
                              value={editForm.multiplier}
                              onChange={(e) => setEditForm({...editForm, multiplier: parseFloat(e.target.value)})}
                              className="w-20 px-2 py-1 border border-input rounded text-sm text-center"
                            />
                          </td>
                          <td className="py-3 px-4">
                            <input
                              type="text"
                              value={editForm.description}
                              onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                              className="w-full px-2 py-1 border border-input rounded text-sm"
                            />
                          </td>
                          <td className="py-3 px-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={savePayType}
                                disabled={savingPayTypes}
                                className="p-1.5 rounded bg-[#4CAF93] text-white hover:bg-[#3d9c7d] disabled:opacity-50"
                                title="Save"
                              >
                                {savingPayTypes ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                              </button>
                              <button
                                onClick={cancelEditPayType}
                                className="p-1.5 rounded bg-slate-200 text-slate-600 hover:bg-slate-300"
                                title="Cancel"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        // View mode
                        <>
                          <td className="py-3 px-4 font-medium text-foreground">
                            {pt.pay_type}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              pt.category === 'Overtime' ? 'bg-orange-100 text-orange-700' :
                              pt.category === 'Shift Differential' ? 'bg-purple-100 text-purple-700' :
                              pt.category === 'Leave' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {pt.category}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Switch
                              checked={pt.premium_flag}
                              onCheckedChange={() => togglePremiumFlag(pt)}
                              disabled={savingPayTypes}
                            />
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className={`font-mono ${pt.multiplier > 1 ? 'text-[#4CAF93] font-medium' : 'text-muted-foreground'}`}>
                              {pt.multiplier.toFixed(2)}x
                            </span>
                          </td>
                          <td className="py-3 px-4 text-muted-foreground text-sm">
                            {pt.description}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <button
                              onClick={() => startEditPayType(pt)}
                              className="p-1.5 rounded text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                              title="Edit"
                            >
                              <Pencil size={14} />
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pay Types Legend */}
          <div className="mt-4 p-4 bg-muted/30 rounded-lg">
            <p className="text-sm font-medium text-foreground mb-2">Understanding Pay Types</p>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li><span className="font-medium text-[#4CAF93]">Premium Flag:</span> When enabled, this pay type is included in overtime/premium pay analysis and recommendations.</li>
              <li><span className="font-medium text-[#4CAF93]">Multiplier:</span> The pay rate multiplier (e.g., 1.5x = time and a half, 2.0x = double time).</li>
              <li><span className="font-medium text-[#4CAF93]">Category:</span> Groups pay types for different recommendation logic (Overtime vs Shift Differential).</li>
            </ul>
          </div>
        </motion.div>

        {/* Insight Thresholds Panel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="kpi-card"
          data-testid="thresholds-section"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
              <Bell size={20} className="text-amber-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-foreground">Insight Thresholds</h2>
              <p className="text-sm text-muted-foreground">
                Tune what counts as a "Critical Alert" or noteworthy insight. Changes apply to all insights and recommendations on next refresh.
              </p>
            </div>
            <button
              onClick={resetThresholdsToDefault}
              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 px-2 py-1 rounded border border-input hover:bg-slate-50"
              data-testid="reset-thresholds-btn"
              title="Reset all to defaults"
            >
              <RotateCcw size={14} /> Reset
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
            {thresholdDefs.map((def) => {
              const current = thresholdEdits[def.id] ?? def.default;
              const isModified = thresholdValues[def.id] !== def.default;
              return (
                <div
                  key={def.id}
                  className={`p-3 rounded-lg border ${isModified ? 'border-amber-300 bg-amber-50/30' : 'border-input'}`}
                  data-testid={`threshold-row-${def.id}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-1">
                    <label className="text-sm font-medium text-foreground flex-1">{def.name}</label>
                    {isModified && (
                      <span className="text-[10px] uppercase tracking-wide bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                        Customized
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">{def.description}</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      step={def.unit === 'x' ? 0.1 : 1}
                      value={current}
                      onChange={(e) =>
                        setThresholdEdits({ ...thresholdEdits, [def.id]: e.target.value })
                      }
                      className="flex-1 px-2 py-1.5 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                      data-testid={`threshold-input-${def.id}`}
                    />
                    <span className="text-xs text-muted-foreground min-w-[60px]">{def.unit}</span>
                    <span className="text-xs text-slate-400">default {def.default}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex justify-end mt-4">
            <button
              onClick={saveThresholds}
              disabled={savingThresholds}
              className="flex items-center gap-2 bg-[#2E5C8A] text-white px-4 py-2 rounded-md text-sm hover:bg-[#1e3a5f] disabled:opacity-50"
              data-testid="save-thresholds-btn"
            >
              {savingThresholds ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save Thresholds
            </button>
          </div>
        </motion.div>

        {/* SQL Server Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="kpi-card"
          data-testid="sql-settings-section"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Database size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">SQL Server Connection</h2>
              <p className="text-sm text-muted-foreground">Configure your SQL Server database connection</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Server Host</label>
              <input
                type="text"
                value={settings.sql_server_host}
                onChange={(e) => handleChange('sql_server_host', e.target.value)}
                placeholder={currentSettings.sql_server_host || 'e.g., server.database.windows.net'}
                className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="sql-host-input"
              />
              <p className="text-xs text-muted-foreground mt-1">Current: {currentSettings.sql_server_host || 'Not set'}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Database Name</label>
              <input
                type="text"
                value={settings.sql_server_database}
                onChange={(e) => handleChange('sql_server_database', e.target.value)}
                placeholder={currentSettings.sql_server_database || 'e.g., timesheets'}
                className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="sql-database-input"
              />
              <p className="text-xs text-muted-foreground mt-1">Current: {currentSettings.sql_server_database || 'Not set'}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Username</label>
                <input
                  type="text"
                  value={settings.sql_server_user}
                  onChange={(e) => handleChange('sql_server_user', e.target.value)}
                  placeholder={currentSettings.sql_server_user || 'Database username'}
                  className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  data-testid="sql-user-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Current: {currentSettings.sql_server_user || 'Not set'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Password</label>
                <input
                  type="password"
                  value={settings.sql_server_password}
                  onChange={(e) => handleChange('sql_server_password', e.target.value)}
                  placeholder="Enter new password (leave blank to keep current)"
                  className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  data-testid="sql-password-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Leave blank to keep existing password</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* AI Chat Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="kpi-card"
          data-testid="ai-settings-section"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Key size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">AI Chat Configuration</h2>
              <p className="text-sm text-muted-foreground">Configure API key for AI chat functionality</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Current Key Status */}
            {currentSettings.is_custom_key && currentSettings.masked_llm_key && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-800">Custom API Key Active</p>
                  <p className="text-sm text-green-600 font-mono">{currentSettings.masked_llm_key}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    if (window.confirm('Remove custom API key and switch back to default?')) {
                      setDeletingKey(true);
                      try {
                        await axios.delete(`${API}/settings/llm-key`);
                        toast.success('Switched to default AI configuration');
                        fetchSettings();
                      } catch (error) {
                        toast.error('Failed to remove API key');
                      } finally {
                        setDeletingKey(false);
                      }
                    }
                  }}
                  disabled={deletingKey}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  {deletingKey ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                  <span className="ml-1">Remove</span>
                </Button>
              </div>
            )}

            {!currentSettings.is_custom_key && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <p className="text-sm font-medium text-slate-700">Using Default Configuration</p>
                <p className="text-xs text-slate-500 mt-1">Enter your own OpenAI API key below to use a custom configuration.</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {currentSettings.is_custom_key ? 'Update API Key' : 'Add OpenAI API Key'}
              </label>
              <div className="relative">
                <input
                  type={showApiKey ? 'text' : 'password'}
                  value={settings.emergent_llm_key}
                  onChange={(e) => handleChange('emergent_llm_key', e.target.value)}
                  placeholder="sk-..."
                  className="w-full px-3 py-2 pr-10 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  data-testid="llm-key-input"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  title={showApiKey ? 'Hide API key' : 'Show API key'}
                >
                  {showApiKey ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Enter a new key and click Save to update. Click the eye icon to show/hide the key.
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-foreground mb-2 font-medium">About AI Chat</p>
              <p className="text-sm text-muted-foreground">
                AI Chat uses GPT to analyze your overtime data and provide insights. 
                You can provide your own OpenAI API key for full control, or use the 
                default configuration.
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" 
                   className="text-[#2E5C8A] hover:underline">
                  Get an OpenAI API key →
                </a>
              </p>
            </div>

            {/* AI Chat Diagnostics */}
            <div className="border border-border rounded-lg p-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Activity size={18} className="text-muted-foreground" />
                  <p className="text-sm font-medium text-foreground">AI Chat Diagnostics</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={runDiagnostics}
                  disabled={runningDiagnostics}
                  data-testid="run-diagnostics-btn"
                >
                  {runningDiagnostics ? (
                    <>
                      <Loader2 size={14} className="animate-spin mr-1" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <RefreshCw size={14} className="mr-1" />
                      Run Test
                    </>
                  )}
                </Button>
              </div>
              
              {diagnostics && (
                <div className="space-y-3 text-sm">
                  {/* Test Result */}
                  <div className={`flex items-center gap-2 p-2 rounded ${
                    diagnostics.test_result === 'SUCCESS' ? 'bg-green-50 text-green-700' :
                    diagnostics.test_result === 'FAILED' ? 'bg-red-50 text-red-700' :
                    'bg-yellow-50 text-yellow-700'
                  }`}>
                    {diagnostics.test_result === 'SUCCESS' ? (
                      <CheckCircle size={16} />
                    ) : diagnostics.test_result === 'FAILED' ? (
                      <XCircle size={16} />
                    ) : (
                      <AlertCircle size={16} />
                    )}
                    <span className="font-medium">
                      {diagnostics.test_result === 'SUCCESS' ? 'AI Chat is working!' :
                       diagnostics.test_result === 'FAILED' ? 'AI Chat test failed' :
                       'Test skipped'}
                    </span>
                  </div>

                  {/* Configuration Details */}
                  <div className="bg-slate-50 rounded p-3 space-y-1 font-mono text-xs">
                    <p><span className="text-muted-foreground">Provider:</span> {diagnostics.configuration?.provider || 'N/A'}</p>
                    <p><span className="text-muted-foreground">Key prefix:</span> {diagnostics.configuration?.key_prefix || 'None'}</p>
                    <p><span className="text-muted-foreground">Using default key:</span> {diagnostics.configuration?.using_default_key ? 'Yes' : 'No'}</p>
                    <p><span className="text-muted-foreground">Libraries:</span> 
                      {diagnostics.libraries?.emergentintegrations ? ' ✓Emergent' : ' ✗Emergent'}
                      {diagnostics.libraries?.openai ? ' ✓OpenAI' : ' ✗OpenAI'}
                      {diagnostics.libraries?.anthropic ? ' ✓Anthropic' : ' ✗Anthropic'}
                    </p>
                  </div>

                  {/* Error Details */}
                  {diagnostics.error && (
                    <div className="bg-red-50 border border-red-200 rounded p-3">
                      <p className="font-medium text-red-800 mb-1">Error: {diagnostics.error_type || 'Unknown'}</p>
                      <p className="text-red-600 text-xs font-mono break-all">{diagnostics.error}</p>
                      {diagnostics.suggestion && (
                        <p className="text-red-700 mt-2 text-xs">💡 {diagnostics.suggestion}</p>
                      )}
                    </div>
                  )}

                  {/* Success Response */}
                  {diagnostics.test_response && diagnostics.test_result === 'SUCCESS' && (
                    <div className="bg-green-50 border border-green-200 rounded p-3">
                      <p className="text-green-700 text-xs font-mono">{diagnostics.test_response}</p>
                    </div>
                  )}
                </div>
              )}

              {!diagnostics && !runningDiagnostics && (
                <p className="text-xs text-muted-foreground">
                  Click "Run Test" to check if AI Chat is properly configured and working.
                </p>
              )}
            </div>
          </div>
        </motion.div>

        {/* Save Button */}
        <div className="flex justify-end pb-24">
          <Button
            onClick={handleSave}
            disabled={saving}
            size="lg"
            data-testid="save-settings-button"
          >
            <Save size={18} />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>

      {/* Premium Toggle Confirmation Dialog (auto-refresh on confirm) */}
      <Dialog open={Boolean(pendingToggle)} onOpenChange={(open) => !open && cancelPendingToggle()}>
        <DialogContent className="sm:max-w-md" data-testid="premium-toggle-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle size={20} className="text-[#FF8C42]" />
              {pendingToggle?.willBePremium
                ? `Mark "${pendingToggle?.payType?.pay_type}" as Premium?`
                : `Remove "${pendingToggle?.payType?.pay_type}" from Premium?`}
            </DialogTitle>
            <DialogDescription className="pt-2">
              This change recalculates <strong>every chart, KPI, filter, and AI recommendation</strong> across the site.
              Premium totals, costs, and trend lines will all update.
            </DialogDescription>
          </DialogHeader>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 my-2">
            <p className="text-sm text-amber-800">
              <strong>Heads up:</strong> Server caches will be cleared and the app will reload to the dashboard
              with refreshed analytics.
            </p>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={cancelPendingToggle}
              disabled={savingPayTypes}
              data-testid="premium-toggle-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmPendingToggle}
              disabled={savingPayTypes}
              className="bg-[#2E5C8A] hover:bg-[#1e3a5f]"
              data-testid="premium-toggle-confirm"
            >
              {savingPayTypes ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Applying...
                </>
              ) : (
                <>
                  <RefreshCw size={16} className="mr-2" />
                  Yes, apply &amp; refresh
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refresh Analytics Confirmation Dialog */}
      <Dialog open={showRefreshDialog} onOpenChange={setShowRefreshDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw size={20} className="text-[#4CAF93]" />
              Refresh Analytics?
            </DialogTitle>
            <DialogDescription className="pt-2">
              You've made changes to pay type configurations. To see these changes reflected in your dashboards, insights, and recommendations, we need to refresh the analytics data.
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 my-2">
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> This may take a moment as all cached data will be cleared and recalculated.
            </p>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowRefreshDialog(false)}
            >
              Later
            </Button>
            <Button
              onClick={handleRefreshAnalytics}
              disabled={refreshing}
              className="bg-[#4CAF93] hover:bg-[#3d9c7d]"
            >
              {refreshing ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Refreshing...
                </>
              ) : (
                <>
                  <RefreshCw size={16} className="mr-2" />
                  Refresh Now
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;
